import React from "react";

import {
  RIBBON_SURFACE,
  getActiveHolidayBanner,
} from "@/app/home/SeasonalLandingBanner";

// Server-renderable eCards hero + supporting sections.
//
// This is the SSR fallback for EcardsNarrativeBanner. The narrative banner
// is a client component (it uses `useSearchParams`), so Next.js renders its
// Suspense fallback in the SSR HTML — crawlers and SEO scanners see whatever
// the fallback emits. On the home page (`/`, the site's primary SEO surface)
// that must include the H1 and hero copy, so this component owns all the
// static text the auditor needs: the H1, hero intro, the "Why Choose Digital
// eCards?" benefits, and the "Real Human Creativity" section.
//
// The same component is re-rendered inside the client banner's expanded state
// after hydration, so the visual transition is seamless. The optional
// `onStartCreating` prop lets the client banner intercept the CTA to collapse
// the banner + persist the preference; without it (the SSR render) the CTA is
// a plain `<a href="#browse">` that works without JS and is crawlable.
//
// Mirrors the FathersDayHero pattern under `app/fathersday/`.

// Pre-made "stick-on" flower bow — a top-down view of a multi-loop
// gift bow like the ones sold at Hallmark. Two concentric layers of
// petals radiating from a central rosette. Each petal uses a
// linearGradient that rotates with it, giving the bow a fluffy
// dimensional look (each loop is lit individually). Sits on top of
// the ribbon at the start of the eCards hero — like sticking a
// pre-made bow onto a wrapped gift.
//
// Sparkle dust around the FlowerBow: each Sparkle is one twinkle in
// the SVG's local coords (bow center at 50,50). Mixed shapes (4-point
// stars + circles) and brand-palette base colors give it a glitter
// feel rather than a uniform halo. Density skews upper-right
// (implied hand sprinkling from that direction) but every quadrant
// gets coverage including a dedicated lower-right arc.
//
// Each entry carries its own `altColor` plus `beginSec`/`durSec` so
// the twinkles animate asynchronously rather than in lockstep — see
// the three-phase comment on BOW_SPARKLES below for the cycle
// itself.
type Sparkle = {
  cx: number;
  cy: number;
  size: number;
  color: string;
  altColor: string;
  beginSec: number;
  durSec: number;
  shape: "star" | "dot";
};

// Alt colors are metallic-mirror tones — silver / gold / bronze /
// white — that contrast sharply with the brand-palette base color
// so each shimmer beat reads as a real "flash" rather than a
// near-same-hue swap.
//   SILVER  #DCDCDC
//   GOLD    #E5C100  (richer than brand yellow #F5B947)
//   BRONZE  #B87333
//   WHITE   #FFFFFF
// Three-phase twinkle: each sparkle cycles base → metallic alt →
// BANNER_BG (disappears, blending into the purple ribbon
// background) → base. Together with the staggered begin/dur values
// this gives a real twinkle feel — flashes of color punctuated by
// brief blink-out moments instead of a steady color drift.
const BANNER_BG = "#6852c0";
// Positions are scaled 1.25x outward from the bow center (50, 50) so
// the arc is wider than the bow itself — easier to read as
// "sprinkled around the bow" rather than "stuck on it". Sparkle
// sizes are unchanged (only the spread grows). Some end up outside
// the 0-100 viewBox — fine because the SVG sets overflow="visible".
//
// The cluster is densest in the upper-right (implied hand position)
// but each quadrant gets representation so the bow isn't visually
// lopsided — including a dedicated lower-right arc.
const BOW_SPARKLES: ReadonlyArray<Sparkle> = [
  // Upper-right cluster (densest — implied hand position)
  { cx: 95, cy: 2.5, size: 4, color: "#F5B947", altColor: "#DCDCDC", beginSec: 0.0, durSec: 3.5, shape: "star" },
  { cx: 102.5, cy: 22.5, size: 3, color: "#F47C7C", altColor: "#E5C100", beginSec: 0.75, durSec: 4, shape: "star" },
  { cx: 85, cy: 10, size: 1.6, color: "#8DACFB", altColor: "#DCDCDC", beginSec: 0.25, durSec: 3, shape: "dot" },
  { cx: 106.25, cy: 42.5, size: 1.4, color: "#FAB0B0", altColor: "#B87333", beginSec: 1.5, durSec: 4.5, shape: "dot" },
  { cx: 87.5, cy: 27.5, size: 1, color: "#FFFFFF", altColor: "#E5C100", beginSec: 1.0, durSec: 3.25, shape: "dot" },
  { cx: 78.75, cy: -2.5, size: 1.2, color: "#6BC4D2", altColor: "#FFFFFF", beginSec: 2.0, durSec: 3.75, shape: "dot" },
  { cx: 98.75, cy: 35, size: 1.4, color: "#F47C7C", altColor: "#DCDCDC", beginSec: 0.4, durSec: 3.5, shape: "dot" },
  { cx: 108.75, cy: 10, size: 1.2, color: "#F5B947", altColor: "#FFFFFF", beginSec: 1.8, durSec: 4, shape: "dot" },
  { cx: 92.5, cy: 15, size: 1, color: "#FAB0B0", altColor: "#E5C100", beginSec: 2.4, durSec: 3.25, shape: "dot" },
  { cx: 100, cy: -5, size: 2.5, color: "#F5B947", altColor: "#FFFFFF", beginSec: 1.2, durSec: 4, shape: "star" },
  { cx: 77.5, cy: 20, size: 1.4, color: "#6BC4D2", altColor: "#DCDCDC", beginSec: 0.6, durSec: 3.5, shape: "dot" },
  { cx: 97.5, cy: 50, size: 1.2, color: "#8DACFB", altColor: "#B87333", beginSec: 2.2, durSec: 4.25, shape: "dot" },
  // Upper arc
  { cx: 62.5, cy: -5, size: 3, color: "#F5B947", altColor: "#FFFFFF", beginSec: 0.4, durSec: 4, shape: "star" },
  { cx: 43.75, cy: 0, size: 1.4, color: "#F47C7C", altColor: "#DCDCDC", beginSec: 1.25, durSec: 3.5, shape: "dot" },
  { cx: 27.5, cy: 7.5, size: 2.5, color: "#8DACFB", altColor: "#E5C100", beginSec: 2.25, durSec: 4.5, shape: "star" },
  { cx: 70, cy: 10, size: 1.6, color: "#FAB0B0", altColor: "#DCDCDC", beginSec: 0.9, durSec: 3.75, shape: "dot" },
  { cx: 52.5, cy: 2.5, size: 1.2, color: "#6BC4D2", altColor: "#E5C100", beginSec: 2.7, durSec: 3.5, shape: "dot" },
  { cx: 35, cy: -7.5, size: 1.4, color: "#F5B947", altColor: "#FFFFFF", beginSec: 1.7, durSec: 4, shape: "dot" },
  // Left arc (medium density)
  { cx: 0, cy: 22.5, size: 1.6, color: "#6BC4D2", altColor: "#B87333", beginSec: 0.5, durSec: 4, shape: "dot" },
  { cx: -5, cy: 45, size: 2.5, color: "#F5B947", altColor: "#DCDCDC", beginSec: 1.75, durSec: 3.5, shape: "star" },
  { cx: 5, cy: 65, size: 1.4, color: "#F47C7C", altColor: "#FFFFFF", beginSec: 2.5, durSec: 3, shape: "dot" },
  { cx: 17.5, cy: -2.5, size: 1.2, color: "#FAB0B0", altColor: "#DCDCDC", beginSec: 0.3, durSec: 3.75, shape: "dot" },
  { cx: 7.5, cy: 15, size: 1, color: "#8DACFB", altColor: "#E5C100", beginSec: 2.1, durSec: 4, shape: "dot" },
  { cx: -10, cy: 32.5, size: 1.2, color: "#F47C7C", altColor: "#B87333", beginSec: 1.5, durSec: 3.5, shape: "dot" },
  { cx: -2.5, cy: 57.5, size: 1.4, color: "#6BC4D2", altColor: "#FFFFFF", beginSec: 2.8, durSec: 4.25, shape: "dot" },
  // Lower-left tail (sparse — far from hand)
  { cx: 10, cy: 90, size: 1.2, color: "#FAB0B0", altColor: "#E5C100", beginSec: 1.1, durSec: 4.25, shape: "dot" },
  { cx: 27.5, cy: 105, size: 1, color: "#F5B947", altColor: "#B87333", beginSec: 3.0, durSec: 3.5, shape: "dot" },
  { cx: 15, cy: 77.5, size: 1, color: "#8DACFB", altColor: "#FFFFFF", beginSec: 2.3, durSec: 4, shape: "dot" },
  // Lower arc (slightly right of center)
  { cx: 57.5, cy: 107.5, size: 1, color: "#8DACFB", altColor: "#DCDCDC", beginSec: 0.85, durSec: 4.5, shape: "dot" },
  { cx: 45, cy: 97.5, size: 1.2, color: "#F5B947", altColor: "#FFFFFF", beginSec: 2.6, durSec: 3.75, shape: "dot" },
  // Lower-right arc — balances density opposite the upper-right cluster
  { cx: 82.5, cy: 82.5, size: 2.5, color: "#F5B947", altColor: "#DCDCDC", beginSec: 1.6, durSec: 4, shape: "star" },
  { cx: 90, cy: 65, size: 1.6, color: "#F47C7C", altColor: "#E5C100", beginSec: 0.7, durSec: 3.5, shape: "dot" },
  { cx: 72.5, cy: 92.5, size: 1.2, color: "#8DACFB", altColor: "#FFFFFF", beginSec: 2.4, durSec: 4.25, shape: "dot" },
  { cx: 97.5, cy: 75, size: 1.4, color: "#6BC4D2", altColor: "#B87333", beginSec: 1.1, durSec: 3.75, shape: "dot" },
  { cx: 107.5, cy: 67.5, size: 1, color: "#FAB0B0", altColor: "#DCDCDC", beginSec: 2.0, durSec: 4, shape: "dot" },
  { cx: 75, cy: 105, size: 1.2, color: "#F5B947", altColor: "#FFFFFF", beginSec: 0.45, durSec: 3.5, shape: "dot" },
  // A few twinkles near the bow body (inner ring of the arc)
  { cx: 65, cy: 57.5, size: 1.2, color: "#FFFFFF", altColor: "#E5C100", beginSec: 1.9, durSec: 4, shape: "dot" },
  { cx: 35, cy: 45, size: 1, color: "#FFFFFF", altColor: "#DCDCDC", beginSec: 2.75, durSec: 3.5, shape: "dot" },
  { cx: 55, cy: 75, size: 1, color: "#FFFFFF", altColor: "#B87333", beginSec: 2.1, durSec: 4, shape: "dot" },
  { cx: 42.5, cy: 62.5, size: 1, color: "#FFFFFF", altColor: "#E5C100", beginSec: 0.65, durSec: 3.25, shape: "dot" },
  { cx: 60, cy: 35, size: 1.2, color: "#FFFFFF", altColor: "#DCDCDC", beginSec: 1.4, durSec: 3.75, shape: "dot" },
  { cx: 47.5, cy: 85, size: 1, color: "#FFFFFF", altColor: "#FFFFFF", beginSec: 2.55, durSec: 4.25, shape: "dot" },
];

// 4-point star path centered at (cx, cy). `s` is the half-extent
// (tip-to-center distance); the "waist" is 30% of that, giving the
// classic twinkle shape.
function sparkleStarPath(cx: number, cy: number, s: number): string {
  const w = s * 0.3;
  return (
    `M${cx} ${cy - s} L${cx + w} ${cy - w} L${cx + s} ${cy} ` +
    `L${cx + w} ${cy + w} L${cx} ${cy + s} L${cx - w} ${cy + w} ` +
    `L${cx - s} ${cy} L${cx - w} ${cy - w} Z`
  );
}

const FlowerBow = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 100 100"
    fill="none"
    aria-hidden="true"
    // overflow="visible" lets the 1.25x-scaled sparkle arc render
    // outside the 0-100 viewBox without being clipped — the bow
    // itself stays inside the viewBox; only the sprinkled dust
    // extends beyond it.
    overflow="visible"
    className="block h-auto w-[110px] drop-shadow-xl md:w-[140px]"
  >
    <defs>
      <linearGradient id="ecards-bow-petal" x1="50%" y1="100%" x2="50%" y2="0%">
        <stop offset="0%" stopColor="#9F2424" />
        <stop offset="35%" stopColor="#EE4545" />
        <stop offset="55%" stopColor="#F89090" />
        <stop offset="80%" stopColor="#EE4545" />
        <stop offset="100%" stopColor="#C73838" />
      </linearGradient>
      <radialGradient id="ecards-bow-center" cx="40%" cy="35%" r="60%">
        <stop offset="0%" stopColor="#FAB0B0" />
        <stop offset="50%" stopColor="#EE4545" />
        <stop offset="100%" stopColor="#7A1818" />
      </radialGradient>
      {/* Soft glow behind each sparkle — gives the dust real twinkle
          against the dark purple background. */}
      <filter id="ecards-sparkle-glow" x="-100%" y="-100%" width="300%" height="300%">
        <feGaussianBlur stdDeviation="0.8" result="blur" />
        <feMerge>
          <feMergeNode in="blur" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
    </defs>
    {/* Outer petal layer: 8 large petals radiating outward */}
    <g transform="translate(50 50)">
      {[0, 45, 90, 135, 180, 225, 270, 315].map((angle) => (
        <ellipse
          key={`outer-${angle}`}
          rx="9"
          ry="22"
          cy="-20"
          fill="url(#ecards-bow-petal)"
          stroke="#7A1818"
          strokeWidth="0.6"
          transform={`rotate(${angle})`}
        />
      ))}
    </g>
    {/* Inner petal layer: 8 smaller, offset 22.5° so they fill the
        gaps between outer petals */}
    <g transform="translate(50 50) rotate(22.5)">
      {[0, 45, 90, 135, 180, 225, 270, 315].map((angle) => (
        <ellipse
          key={`inner-${angle}`}
          rx="7"
          ry="15"
          cy="-12"
          fill="url(#ecards-bow-petal)"
          stroke="#7A1818"
          strokeWidth="0.5"
          transform={`rotate(${angle})`}
        />
      ))}
    </g>
    {/* Center rosette button */}
    <circle
      cx="50"
      cy="50"
      r="7"
      fill="url(#ecards-bow-center)"
      stroke="#7A1818"
      strokeWidth="0.8"
    />
    {/* Tiny offset highlight on the center — sells the "sphere" shape */}
    <circle cx="47.5" cy="47.5" r="2" fill="#FAB0B0" opacity="0.6" />
    {/* Sparkle dust — rendered LAST so it sits on top of the petals
        and is visible both on and around the bow. Each sparkle has a
        SMIL <animate> that cycles its fill through the three-phase
        sequence (base → metallic alt → banner purple blink-out →
        base) on its own staggered begin/dur, so the cluster
        twinkles asynchronously rather than pulsing in unison. */}
    {BOW_SPARKLES.map((s, i) => {
      const animate = (
        <animate
          attributeName="fill"
          // Three-phase: base → metallic alt → banner background
          // (disappears) → base. The blink-out beat is what sells
          // the twinkle; just hue-cycling between two visible
          // colors reads as a drift, not a sparkle.
          values={`${s.color};${s.altColor};${BANNER_BG};${s.color}`}
          dur={`${s.durSec}s`}
          begin={`${s.beginSec}s`}
          repeatCount="indefinite"
        />
      );
      return s.shape === "star" ? (
        <path
          key={`sparkle-${i}`}
          d={sparkleStarPath(s.cx, s.cy, s.size)}
          fill={s.color}
          filter="url(#ecards-sparkle-glow)"
        >
          {animate}
        </path>
      ) : (
        <circle
          key={`sparkle-${i}`}
          cx={s.cx}
          cy={s.cy}
          r={s.size}
          fill={s.color}
          filter="url(#ecards-sparkle-glow)"
        >
          {animate}
        </circle>
      );
    })}
  </svg>
);

// Icons for the "Real Human Creativity" bullets. Borrowed from the
// Father's Day hero's sticker set for the same warmth and color — a
// purple outline (#6852c0) with coral/gold/teal fills and little
// sparkle accents, instead of a flat monochrome glyph. Kept local to
// this hero, mirroring the FathersDayHero pattern where each hero owns
// its icon set. Each sits bare in the bullet row at h-8 w-8.
const BULLET_ICON_CLASS = "h-8 w-8 flex-shrink-0";

// Paintbrush — the artists/writers/poets bullet.
const PaintbrushIcon = () => (
  <svg viewBox="0 0 32 32" fill="none" className={BULLET_ICON_CLASS} aria-hidden="true">
    <path d="M4 10 Q 16 2, 28 9" stroke="#F47C7C" strokeWidth="3" strokeLinecap="round" fill="none" />
    <path d="M3 28 L6 31 L17 20 L14 17 Z" fill="#FFFCEB" stroke="#6852c0" strokeWidth="1.5" strokeLinejoin="round" />
    <path d="M14 17 L17 20 L20 17 L17 14 Z" fill="#F5B947" stroke="#6852c0" strokeWidth="1.5" strokeLinejoin="round" />
    <path d="M17 14 L20 17 L24 11 Z" fill="#F47C7C" stroke="#6852c0" strokeWidth="1.5" strokeLinejoin="round" />
  </svg>
);

// Polaroid camera — the "make it yours" (photos & videos) bullet.
const PolaroidCameraIcon = () => (
  <svg viewBox="0 0 48 48" fill="none" className={BULLET_ICON_CLASS} aria-hidden="true">
    <rect x="6" y="14" width="36" height="28" rx="3" fill="#FFFFFF" stroke="#6852c0" strokeWidth="2.5" />
    <rect x="16" y="9" width="14" height="6" rx="1.5" fill="#FFFFFF" stroke="#6852c0" strokeWidth="2" />
    <circle cx="24" cy="28" r="8" fill="#6BC4D2" stroke="#6852c0" strokeWidth="2.5" />
    <circle cx="24" cy="28" r="3.5" fill="#FFFFFF" stroke="#6852c0" strokeWidth="1.5" />
    <rect x="33" y="18" width="5" height="3.5" rx="0.5" fill="#F5B947" />
  </svg>
);

// Coupon/ticket — the "free to start creating" bullet.
const CouponIcon = () => (
  <svg viewBox="0 0 32 32" fill="none" className={BULLET_ICON_CLASS} aria-hidden="true">
    <rect x="3" y="9" width="26" height="14" rx="1.5" fill="#FFFFFF" stroke="#6852c0" strokeWidth="1.5" />
    <line x1="11" y1="10" x2="11" y2="22" stroke="#6852c0" strokeWidth="1.5" strokeDasharray="2 2" strokeLinecap="round" />
    <path d="M7 13 L7 17 M5 15 L9 15" stroke="#F5B947" strokeWidth="2" strokeLinecap="round" />
    <path d="M20 17 C18 15.5, 15.5 16, 15.5 18 C15.5 20.5, 20 22, 20 22 C20 22, 24.5 20.5, 24.5 18 C24.5 16, 22 15.5, 20 17 Z" fill="#F47C7C" stroke="#6852c0" strokeWidth="1" strokeLinejoin="round" />
  </svg>
);

// Heart — the "every moment that matters" bullet.
const HeartIcon = () => (
  <svg viewBox="0 0 32 32" fill="none" className={BULLET_ICON_CLASS} aria-hidden="true">
    <path d="M16 27 C4 18, 4 9, 11 9 C14 9, 16 12, 16 12 C16 12, 18 9, 21 9 C28 9, 28 18, 16 27 Z" fill="#F47C7C" stroke="#6852c0" strokeWidth="2" strokeLinejoin="round" />
    <circle cx="5" cy="6" r="1.5" fill="#F5B947" />
    <circle cx="27" cy="6" r="1.5" fill="#F5B947" />
  </svg>
);

// The four "Real Human Creativity" bullets, paired with a meaningful
// icon each. Data-driven so copy/icon edits live in one place and the
// JSX stays a single map instead of four near-identical blocks.
const CREATIVITY_POINTS: ReadonlyArray<{
  Icon: React.ComponentType;
  title: string;
  body: string;
}> = [
  {
    Icon: PaintbrushIcon,
    title: "Artists, Writers & Poets",
    body: "Every animation is handcrafted by real human artists who pour their creativity into making your message unforgettable.",
  },
  {
    Icon: PolaroidCameraIcon,
    title: "Make It Yours",
    body: "Add your own photos, videos, and heartfelt words. Each Wrap Experience becomes uniquely meaningful to your loved ones.",
  },
  {
    Icon: CouponIcon,
    title: "Free to Start Creating",
    body: "Explore our collection of beautiful templates and start sending joy today. No barriers between you and making someone's day.",
  },
  {
    Icon: HeartIcon,
    title: "For Every Moment That Matters",
    body: "Birthdays, holidays, thank you, graduations, and more. Plus blank templates you can adapt for any occasion worth celebrating.",
  },
];

interface EcardsHeroProps {
  // When provided, the "Start Creating" CTA renders as a button that
  // calls this handler (the client banner uses it to collapse the
  // banner + persist the preference). When absent — the SSR fallback
  // render — the CTA is a plain `<a href="#browse">` so it is
  // crawlable and works without JS.
  onStartCreating?: () => void;
  // When provided, intercepts the FAQ link click for smooth scroll and
  // reduced-motion handling. Absent (SSR) → the link is a plain
  // `<a href="#faq">` that still jumps to the FAQ without JS.
  onFaqClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
}

export default function EcardsHero({
  onStartCreating,
  onFaqClick,
}: EcardsHeroProps = {}) {
  // Pick up the active holiday's background so this hero stays visually
  // continuous with SeasonalLandingBanner above. Falls back to brand
  // purple outside any holiday window.
  const headerBgClass =
    getActiveHolidayBanner()?.bgClassName ?? "bg-wg-purple";

  const ctaClassName =
    "inline-block rounded-lg bg-white px-6 py-2 text-lg font-semibold text-wg-purple transition-colors hover:bg-gray-100";

  return (
    <>
      {/* Hero Section */}
      <section className={`${headerBgClass} text-white`}>
        {/* Outer max-w-6xl matches SeasonalLandingBanner so the ribbon
            lands at the same horizontal x on both banners. The inner
            max-w-4xl text-center preserves the hero's narrower
            centered copy. */}
        <div className="relative mx-auto max-w-6xl py-12 pl-16 pr-6">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute bottom-0 left-0 top-0 w-[40px]"
            style={{ background: RIBBON_SURFACE }}
          />
          {/* Stick-on flower bow centered on the ribbon at the start
              of the hero — like a pre-made bow stuck onto the gift's
              ribbon. Sits slightly below the section's top so it's
              clearly on the eCards banner (not overlapping anything
              above). */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute left-[20px] top-3 z-10 -translate-x-1/2 md:top-4"
          >
            <FlowerBow />
          </div>
          <div className="mx-auto max-w-4xl text-center">
            <h1 className="mb-4 text-4xl font-bold md:text-5xl">
              Animated eCards That Make Moments Magical
            </h1>
            <p className="mb-8 text-lg text-white/90 md:text-xl">
              Send animated eCards that spark joy and create cherished memories.
              Crafted by real human artists, delivered instantly to the people
              you love.
            </p>
            {onStartCreating ? (
              <button
                onClick={onStartCreating}
                className={ctaClassName}
              >
                Start Creating
              </button>
            ) : (
              <a href="#browse" className={ctaClassName}>
                Start Creating
              </a>
            )}
            <p className="mt-6 text-sm text-white/85">
              <a
                href="#faq"
                onClick={onFaqClick}
                className="inline-flex items-center gap-1 underline decoration-white/40 underline-offset-4 transition-colors hover:text-white hover:decoration-white"
              >
                New to eCards? Have a question? Read the FAQs
                <span aria-hidden="true">↓</span>
              </a>
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="bg-wg-bg py-12">
        <div className="mx-auto max-w-6xl px-6">
          <h2 className="mb-8 text-center text-2xl font-bold text-gray-900 md:text-3xl">
            Why Choose Digital eCards?
          </h2>
          <div className="grid gap-6 md:grid-cols-3">
            {/* Benefit 1 */}
            <div className="rounded-lg border-4 border-wg-purple bg-[#ece5ff] p-6">
              <div className="mb-4 text-4xl">🌱</div>
              <h3 className="mb-2 text-lg font-semibold text-wg-purple">
                Zero Waste, 100% Joy
              </h3>
              <p className="text-[#4a4a4a]">
                Gift-givers who care about the planet choose eCards. Nothing
                left behind but smiles and cherished memories.
              </p>
            </div>

            {/* Benefit 2 */}
            <div className="rounded-lg border-4 border-wg-purple bg-[#ece5ff] p-6">
              <div className="mb-4 text-4xl">💜</div>
              <h3 className="mb-2 text-lg font-semibold text-wg-purple">
                Deeply Personal
              </h3>
              <p className="text-[#4a4a4a]">
                Add your own photos, videos, and heartfelt messages. Create a
                Wrap Experience that&apos;s uniquely meaningful to the people
                you love.
              </p>
            </div>

            {/* Benefit 3 */}
            <div className="rounded-lg border-4 border-wg-purple bg-[#ece5ff] p-6">
              <div className="mb-4 text-4xl">✨</div>
              <h3 className="mb-2 text-lg font-semibold text-wg-purple">
                Emotion in Motion
              </h3>
              <p className="text-[#4a4a4a]">
                Beautiful animations crafted by real human artists bring your
                message to life in ways that static cards simply can&apos;t
                match.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What Makes Us Different */}
      <section className="bg-white py-12">
        <div className="mx-auto max-w-4xl px-6">
          <h2 className="mb-8 text-center text-2xl font-bold text-wg-purple md:text-3xl">
            Real Human Creativity
          </h2>
          <div className="space-y-4">
            {CREATIVITY_POINTS.map((point) => (
              <div key={point.title} className="flex items-start gap-4">
                <point.Icon />
                <div>
                  <h3 className="font-semibold text-wg-purple">
                    {point.title}
                  </h3>
                  <p className="text-[#4a4a4a]">{point.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
